import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { paths } from '@ers/sdk';
import { api, call } from './client';
import { qk } from './keys';

export function useScopes() {
  return useQuery({
    queryKey: qk.scopes,
    queryFn: async () => (await call(api.GET('/api/v1/meta/scopes'))).data,
    staleTime: Infinity,
  });
}

export function useEventTypes() {
  return useQuery({
    queryKey: qk.eventTypes,
    queryFn: async () => (await call(api.GET('/api/v1/meta/event-types'))).data,
    staleTime: 10 * 60_000,
  });
}

export function useTokens() {
  return useQuery({
    queryKey: qk.tokens,
    queryFn: async () => (await call(api.GET('/api/v1/tokens'))).data,
  });
}

type TokenRequest = NonNullable<paths['/api/v1/tokens']['post']['requestBody']>['content']['application/json'];
export type TokenScope = TokenRequest['scopes'][number];
export type CreateTokenBody = {
  name: string;
  kind: 'personal' | 'service';
  purpose?: string;
  scopes: TokenScope[];
  expiresInDays: number;
};

export function useCreateToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTokenBody) => call(api.POST('/api/v1/tokens', { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.tokens }),
  });
}

export function useRevokeToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => call(api.DELETE('/api/v1/tokens/{id}', { params: { path: { id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.tokens }),
  });
}

export function useWebhooks(enabled = true) {
  return useQuery({
    queryKey: qk.webhooks,
    queryFn: async () => (await call(api.GET('/api/v1/webhooks'))).data,
    enabled,
  });
}

export type WebhookBody = { name: string; url: string; eventTypes: string[]; projectIds: string[] };

export function useCreateWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: WebhookBody) => call(api.POST('/api/v1/webhooks', { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.webhooks }),
  });
}

export function usePatchWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<WebhookBody> & { active?: boolean } }) =>
      call(api.PATCH('/api/v1/webhooks/{id}', { params: { path: { id } }, body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.webhooks }),
  });
}

export function useDisableWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => call(api.DELETE('/api/v1/webhooks/{id}', { params: { path: { id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.webhooks }),
  });
}

export function useTestWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => call(api.POST('/api/v1/webhooks/{id}/test', { params: { path: { id } } })),
    onSuccess: (_r, id) => {
      window.setTimeout(() => void qc.invalidateQueries({ queryKey: qk.deliveries(id) }), 1500);
    },
  });
}

export function useRedeliver() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, fromEventId, toEventId }: { id: string; fromEventId: number; toEventId?: number }) =>
      call(api.POST('/api/v1/webhooks/{id}/redeliver', { params: { path: { id } }, body: { fromEventId, toEventId } })),
    onSuccess: (_r, v) => qc.invalidateQueries({ queryKey: qk.deliveries(v.id) }),
  });
}

export function useDeliveries(id: string | null) {
  return useQuery({
    queryKey: qk.deliveries(id ?? ''),
    queryFn: async () => (await call(api.GET('/api/v1/webhooks/{id}/deliveries', { params: { path: { id: id! } } }))).data,
    enabled: !!id,
    refetchInterval: 10_000,
  });
}

export function useCreateCalendarFeed() {
  return useMutation({
    mutationFn: () => call(api.POST('/api/v1/calendar-feeds')),
  });
}

export function useRevokeCalendarFeed() {
  return useMutation({
    mutationFn: (id: string) => call(api.DELETE('/api/v1/calendar-feeds/{id}', { params: { path: { id } } })),
  });
}

export function useSearch(q: string) {
  const term = q.trim();
  return useQuery({
    queryKey: qk.search(term),
    queryFn: () => call(api.GET('/api/v1/search', { params: { query: { q: term, limit: 20 } } })),
    enabled: term.length >= 2,
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}
